/**
 * Lightweight lead journey tracking: demo page visits and the trail of public
 * pages a visitor opens. Persisted in localStorage so lead/WhatsApp submissions
 * can include behavior context beyond first/last attribution snapshots.
 */
import {
  LEAD_PAGE_TRAIL_LIMIT,
  leadPagePath,
  type LeadPageVisit,
} from '@shared/leadPages';

const STORAGE_KEY = 'deepthink_lead_journey';

interface JourneyPayload {
  visitedDemoPractice: boolean;
  visitedDemoAnalytics: boolean;
  visitedAnyDemo: boolean;
  demoPracticeFirstSeenAt: string | null;
  demoPracticeLastSeenAt: string | null;
  demoAnalyticsFirstSeenAt: string | null;
  demoAnalyticsLastSeenAt: string | null;
  pages: LeadPageVisit[];
  updatedAt: string | null;
}

interface StoredJourney {
  visitedDemoPractice?: boolean;
  visitedDemoAnalytics?: boolean;
  visitedAnyDemo?: boolean;
  demoPracticeFirstSeenAt?: string | null;
  demoPracticeLastSeenAt?: string | null;
  demoAnalyticsFirstSeenAt?: string | null;
  demoAnalyticsLastSeenAt?: string | null;
  pages?: LeadPageVisit[];
  updatedAt?: string | null;
}

const normalizePathname = (pathname: string | null | undefined): string => {
  if (typeof pathname !== 'string') {
    return '';
  }

  const trimmed = pathname.trim();
  if (!trimmed) {
    return '';
  }

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return new URL(trimmed).pathname.toLowerCase();
    }
  } catch {
    // Fall through to simple parsing for malformed URLs.
  }

  const pathOnly = trimmed.split('?')[0].split('#')[0].trim().toLowerCase();
  if (!pathOnly) {
    return '';
  }

  return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
};

const isDemoPracticePath = (pathname: string): boolean =>
  pathname.startsWith('/demo/practice');

const isDemoAnalyticsPath = (pathname: string): boolean =>
  pathname.startsWith('/demo/analytics');

const storedPages = (journey: StoredJourney | null): LeadPageVisit[] =>
  Array.isArray(journey?.pages)
    ? journey.pages.filter(
        (visit): visit is LeadPageVisit =>
          typeof visit?.path === 'string' && typeof visit?.at === 'string'
      )
    : [];

const readStoredJourney = (): StoredJourney | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return parsed as StoredJourney;
  } catch (error) {
    console.warn('Failed to read lead journey data:', error);
    return null;
  }
};

const writeStoredJourney = (journey: StoredJourney): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(journey));
  } catch (error) {
    console.warn('Failed to persist lead journey data:', error);
  }
};

const toApiJourneyPayload = (
  journey: StoredJourney | null
): JourneyPayload | null => {
  if (!journey || typeof journey !== 'object') {
    return null;
  }

  const visitedDemoPractice = Boolean(journey.visitedDemoPractice);
  const visitedDemoAnalytics = Boolean(journey.visitedDemoAnalytics);
  const visitedAnyDemo =
    visitedDemoPractice ||
    visitedDemoAnalytics ||
    Boolean(journey.visitedAnyDemo);
  const pages = storedPages(journey);

  if (!visitedAnyDemo && pages.length === 0) {
    return null;
  }

  return {
    visitedDemoPractice,
    visitedDemoAnalytics,
    visitedAnyDemo,
    demoPracticeFirstSeenAt: journey.demoPracticeFirstSeenAt || null,
    demoPracticeLastSeenAt: journey.demoPracticeLastSeenAt || null,
    demoAnalyticsFirstSeenAt: journey.demoAnalyticsFirstSeenAt || null,
    demoAnalyticsLastSeenAt: journey.demoAnalyticsLastSeenAt || null,
    pages,
    updatedAt: journey.updatedAt || null,
  };
};

/**
 * Records the current route for lightweight journey tracking: a demo visit,
 * and a public page the trail has not seen yet. A page keeps its place from
 * its first view; past the limit the oldest page drops off.
 * Returns the persisted journey payload (or null when nothing was recorded).
 */
export const recordLeadJourneyPath = (
  pathname?: string
): JourneyPayload | null => {
  const fallbackPathname =
    typeof window !== 'undefined' ? window.location?.pathname || '' : '';
  const normalizedPath = normalizePathname(pathname || fallbackPathname);
  if (!normalizedPath) {
    return toApiJourneyPayload(readStoredJourney());
  }

  const visitedDemoPractice = isDemoPracticePath(normalizedPath);
  const visitedDemoAnalytics = isDemoAnalyticsPath(normalizedPath);
  const visitedDemo = visitedDemoPractice || visitedDemoAnalytics;
  const existing: StoredJourney = readStoredJourney() || {};
  const pages = storedPages(existing);
  const page = leadPagePath(normalizedPath);
  const newPage =
    page && !pages.some(visit => visit.path === page) ? page : null;

  if (!visitedDemo && !newPage) {
    return toApiJourneyPayload(existing);
  }

  const nowIso = new Date().toISOString();
  const next: StoredJourney = {
    ...existing,
    ...(newPage !== null && {
      pages: [...pages, { path: newPage, at: nowIso }].slice(
        -LEAD_PAGE_TRAIL_LIMIT
      ),
    }),
    ...(visitedDemo && {
      visitedDemoPractice:
        Boolean(existing.visitedDemoPractice) || visitedDemoPractice,
      visitedDemoAnalytics:
        Boolean(existing.visitedDemoAnalytics) || visitedDemoAnalytics,
      demoPracticeFirstSeenAt:
        existing.demoPracticeFirstSeenAt ||
        (visitedDemoPractice ? nowIso : null),
      demoPracticeLastSeenAt: visitedDemoPractice
        ? nowIso
        : existing.demoPracticeLastSeenAt || null,
      demoAnalyticsFirstSeenAt:
        existing.demoAnalyticsFirstSeenAt ||
        (visitedDemoAnalytics ? nowIso : null),
      demoAnalyticsLastSeenAt: visitedDemoAnalytics
        ? nowIso
        : existing.demoAnalyticsLastSeenAt || null,
    }),
    updatedAt: nowIso,
  };

  if (visitedDemo) {
    next.visitedAnyDemo =
      Boolean(next.visitedDemoPractice) || Boolean(next.visitedDemoAnalytics);
  }

  writeStoredJourney(next);
  return toApiJourneyPayload(next);
};

/**
 * Retrieves lead journey payload for API submission.
 * Returns null when no demo visit or public page has been observed.
 */
export const getLeadJourneyData = (): JourneyPayload | null => {
  return toApiJourneyPayload(readStoredJourney());
};

/**
 * Clears stored lead journey data. Primarily for tests.
 */
export const clearLeadJourneyData = (): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear lead journey data:', error);
  }
};
